import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtDate, today, daysAgo } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import EmptyState from "@/components/common/EmptyState";
import { ScrollText } from "lucide-react";

export default function GeneralLedger() {
    const [params, setParams] = useSearchParams();
    const [accounts, setAccounts] = useState([]);
    const [accountId, setAccountId] = useState(params.get("account") || "");
    const [from, setFrom] = useState(daysAgo(180));
    const [to, setTo] = useState(today());
    const [data, setData] = useState(null);

    useEffect(() => { api.get("/coa").then(({ data }) => { setAccounts(data); if (!accountId && data[0]) setAccountId(data[0].id); }); }, []);

    useEffect(() => {
        if (!accountId) return;
        api.get("/general-ledger", { params: { account_id: accountId, date_from: from, date_to: to } }).then(({ data }) => setData(data));
        setParams({ account: accountId });
    }, [accountId, from, to]);

    return (
        <>
            <PageHeader kicker="ACCOUNTING" title="General Ledger" subtitle="Every posting that touched this account."
                actions={
                    <>
                        <Select value={accountId} onValueChange={setAccountId}>
                            <SelectTrigger className="w-[260px]" data-testid="gl-account"><SelectValue placeholder="Pick account" /></SelectTrigger>
                            <SelectContent className="max-h-[400px]">{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} • {a.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[140px]" />
                        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[140px]" />
                    </>
                }
            />
            <PageBody>
                {data && (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <Stat label="Account" value={`${data.account.code} • ${data.account.name}`} />
                            <Stat label="Type" value={data.account.type} mono />
                            <Stat label="Period" value={`${fmtDate(from)} → ${fmtDate(to)}`} />
                            <Stat label="Closing balance" value={fmtMoney(data.closing_balance)} accent="positive" />
                        </div>
                        <div className="bg-card border border-border rounded-md overflow-hidden">
                            {data.rows.length === 0 ? <EmptyState icon={ScrollText} title="No transactions in this period" /> :
                                <table className="fin-table">
                                    <thead><tr><th>Date</th><th>Journal</th><th>Description</th><th>Reference</th><th className="num">Debit</th><th className="num">Credit</th><th className="num">Balance</th></tr></thead>
                                    <tbody>
                                        {data.rows.map((r, i) => (
                                            <tr key={i}>
                                                <td className="text-[12px] text-muted-foreground">{fmtDate(r.date)}</td>
                                                <td className="font-mono text-[12px]">{r.journal_number}</td>
                                                <td>{r.narration}</td>
                                                <td className="text-[12px] text-muted-foreground font-mono">{r.reference || "—"}</td>
                                                <td className="num">{r.debit ? fmtMoney(r.debit) : "—"}</td>
                                                <td className="num">{r.credit ? fmtMoney(r.credit) : "—"}</td>
                                                <td className="num font-medium">{fmtMoney(r.balance)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>}
                        </div>
                    </>
                )}
            </PageBody>
        </>
    );
}

function Stat({ label, value, accent, mono }) {
    return (
        <div className="bg-card border border-border rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
            <div className={`mt-1 ${mono ? "font-mono" : "font-serif"} text-[16px] ${accent === "positive" ? "text-positive" : ""}`}>{value}</div>
        </div>
    );
}
